/**
 * ChatPanel Component
 *
 * AI chat interface with streaming support. Calls `/api/chat/models` on mount
 * to populate the model dropdown from the tenant LLM router's live registry,
 * then streams replies via `api.stream('/chat', ...)`.
 */
import { useState, useRef, useEffect } from 'react';
import {
  Send,
  StopCircle,
  Settings,
  Trash2,
  AtSign,
  FileCode,
  BookOpen,
} from 'lucide-react';
import { useChatStore } from '@/stores/chatStore';
import { useProjectStore } from '@/stores/projectStore';
import { api } from '@/services/api';
import { MarkdownRenderer } from '@/components/markdown/MarkdownRenderer';
import type { ChatMessage } from '@/types';
import clsx from 'clsx';
import { v4 as uuid } from 'uuid';
import { flattenFileTreePaths } from './chatFileFlatten';

interface ApiModel {
  id: string;
  name?: string;
  type?: string;
  status?: string;
  has_api_key?: boolean;
}

interface ModelsResponse {
  object?: string;
  data: ApiModel[];
}

interface StreamChunk {
  content?: string;
  error?: string;
  // 2026-05-06 truncation-bundle Fix #2: vibe-backend forwards vLLM's
  // finish_reason on non-"stop" completions ("length" cap, "tool_calls",
  // "content_filter"). Stored on the assistant message + drives the
  // truncation warning + FileWriteCard Apply-button disable.
  finish_reason?: string;
}

export function ChatPanelLegacy() {
  const {
    messages,
    isStreaming,
    addMessage,
    appendToMessage,
    updateMessage,
    startStreaming,
    stopStreaming,
    clearMessages,
  } = useChatStore();

  const { activeFileId, openFiles, fileTree } = useProjectStore();

  const [input, setInput] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [models, setModels] = useState<ApiModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('auto');
  const [modelsError, setModelsError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // 2026-05-06: pre-fix every chunk during streaming forced scroll-to-bottom,
  // so the user could not scroll up to read earlier text mid-response. Now
  // we only auto-scroll when the user is "anchored" (within 80px of the
  // bottom). Scrolling up de-anchors; scrolling back near the bottom
  // re-anchors. The threshold is generous because smooth-scrolling itself
  // can put us 30-50px shy of true bottom for a frame.
  const ANCHOR_THRESHOLD_PX = 80;
  const [isAnchoredToBottom, setIsAnchoredToBottom] = useState(true);

  // Auto-scroll to bottom — but ONLY if the user is anchored. If they've
  // scrolled up to read earlier content, leave the scroll position alone.
  useEffect(() => {
    if (!isAnchoredToBottom) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isAnchoredToBottom]);

  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsAnchoredToBottom(distanceFromBottom <= ANCHOR_THRESHOLD_PX);
  };

  const jumpToBottom = () => {
    setIsAnchoredToBottom(true);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch the tenant LLM router's live model catalog on mount.
  useEffect(() => {
    let cancelled = false;
    api
      .get<ModelsResponse>('/chat/models')
      .then((payload) => {
        if (cancelled) return;
        const list = Array.isArray(payload?.data) ? payload.data : [];
        setModels(list);
        if (list.length > 0) {
          const hasAuto = list.some((m) => m.id === 'auto');
          setSelectedModel(hasAuto ? 'auto' : list[0].id);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setModelsError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Get current file context
  const activeFile = openFiles.find((f) => f.id === activeFileId);

  // E.21.32 — chip insertion. Inserts a template at the textarea's cursor
  // (when textareaRef is mounted) or appends to end (fallback). For `@file`
  // we substitute the active filename when one is open; otherwise a literal
  // `@` prompts the user to type a path.
  const insertChipTemplate = (kind: '@file' | '/generate' | '@docs') => {
    let template: string;
    if (kind === '@file') {
      template = activeFile ? `@${activeFile.name} ` : '@';
    } else if (kind === '/generate') {
      template = '/generate ';
    } else {
      template = '@docs ';
    }

    const ta = textareaRef.current;
    if (ta && typeof ta.selectionStart === 'number') {
      const start = ta.selectionStart;
      const end = ta.selectionEnd ?? start;
      const next = input.slice(0, start) + template + input.slice(end);
      setInput(next);
      // Restore cursor after the inserted template on next paint.
      requestAnimationFrame(() => {
        const newPos = start + template.length;
        ta.focus();
        ta.setSelectionRange(newPos, newPos);
      });
    } else {
      setInput((prev) => prev + template);
    }
  };

  const handleSubmit = async () => {
    if (!input.trim() || isStreaming) return;

    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      content: input,
      timestamp: new Date().toISOString(),
    };
    addMessage(userMessage);
    setInput('');

    // Build the conversation payload BEFORE the assistant placeholder is added.
    const conversationForLLM = [...messages, userMessage].map(({ role, content }) => ({
      role,
      content,
    }));

    const assistantMessageId = uuid();
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true,
    };
    addMessage(assistantMessage);
    startStreaming(assistantMessageId);

    try {
      const payload: Record<string, unknown> = {
        messages: conversationForLLM,
        model: selectedModel,
        stream: true,
      };
      // 2026-05-06: Pre-fix the chat passed only `name` + `language` so the
      // LLM literally said "I cannot see the files in the file nav". Now we
      // flatten the file tree to a list of paths AND ship the active file's
      // full source so the LLM can read + edit it.
      const treePaths = flattenFileTreePaths(fileTree);
      const context: Record<string, unknown> = {};
      if (activeFile) {
        context.activeFile = {
          name: activeFile.name,
          path: activeFile.path,
          language: activeFile.language ?? 'unknown',
          content: activeFile.content,
        };
      }
      if (treePaths.length > 0) {
        context.fileTree = treePaths;
      }
      if (Object.keys(context).length > 0) {
        payload.context = context;
      }
      for await (const chunk of api.stream<StreamChunk>('/chat', payload)) {
        if (chunk.error) {
          appendToMessage(assistantMessageId, `\n\n*Error: ${chunk.error}*`);
          break;
        }
        if (chunk.content) {
          appendToMessage(assistantMessageId, chunk.content);
        }
        // 2026-05-06 truncation-bundle Fix #2: backend forwards
        // finish_reason on non-"stop" completions. Store on the message
        // so MarkdownRenderer + FileWriteCard can disable Apply on a
        // truncated file block, AND append a visible inline warning so
        // the user sees the truncation in the rendered chat output.
        if (chunk.finish_reason) {
          updateMessage(assistantMessageId, { finishReason: chunk.finish_reason });
          if (chunk.finish_reason === 'length') {
            appendToMessage(
              assistantMessageId,
              '\n\n⚠ **Response truncated — output token limit reached.** ' +
                'Ask "continue" to extend.',
            );
          } else if (chunk.finish_reason !== 'stop') {
            appendToMessage(
              assistantMessageId,
              `\n\n⚠ **Response ended (${chunk.finish_reason}).**`,
            );
          }
        }
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      appendToMessage(assistantMessageId, `\n\n*Error: ${msg}*`);
    } finally {
      stopStreaming();
    }
  };

  const handleStopStreaming = () => {
    stopStreaming();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-vibe-border">
        <span className="text-sm font-medium">AI Chat</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowConfig(!showConfig)}
            className="p-1.5 rounded hover:bg-vibe-bg-tertiary text-vibe-text-secondary"
            title="Settings"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={clearMessages}
            className="p-1.5 rounded hover:bg-vibe-bg-tertiary text-vibe-text-secondary"
            title="Clear chat"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Model Config (collapsible) */}
      {showConfig && (
        <div className="p-3 border-b border-vibe-border bg-vibe-bg-tertiary text-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-vibe-text-secondary">Model</span>
            <select
              className="bg-vibe-bg text-vibe-text rounded px-2 py-1 text-xs"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
            >
              {models.length === 0 && !modelsError && (
                <option value="auto">Loading…</option>
              )}
              {models.length === 0 && modelsError && (
                <option value="auto">auto (router catalog unreachable)</option>
              )}
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name || m.id}
                </option>
              ))}
            </select>
          </div>
          {modelsError && (
            <div className="text-xs text-vibe-error mb-2">
              Failed to load models: {modelsError}
            </div>
          )}
          <div className="text-xs text-vibe-text-secondary">
            {activeFile && (
              <span>
                Context: <code>{activeFile.name}</code>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        className="flex-1 overflow-auto bg-vibe-bg p-4 relative"
      >
        {!isAnchoredToBottom && messages.length > 0 && (
          <button
            type="button"
            onClick={jumpToBottom}
            className="sticky bottom-2 left-1/2 -translate-x-1/2 ml-[40%] z-10 px-3 py-1.5 rounded-full bg-vibe-accent text-white text-xs shadow-vibe-md hover:bg-vibe-accent-hover"
            title="Jump to latest"
          >
            ↓ Jump to latest
          </button>
        )}
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center text-vibe-text-secondary">
              <div className="text-4xl mb-4">{'🤖'}</div>
              <div className="font-medium">How can I help?</div>
              <div className="text-sm mt-2">
                Ask me to generate code, explain<br />
                concepts, or refactor your code.
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={clsx(
                  'flex gap-3',
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                <div
                  className={clsx(
                    'max-w-[85%] rounded-lg p-3 text-sm',
                    message.role === 'user'
                      ? 'bg-vibe-accent text-white'
                      : 'bg-vibe-bg-tertiary text-vibe-text'
                  )}
                >
                  {message.role === 'assistant' ? (
                    <div className="text-sm leading-relaxed">
                      <MarkdownRenderer
                        content={message.content}
                        messageFinishReason={message.finishReason}
                        messageIsStreaming={message.isStreaming}
                      />
                      {message.isStreaming && (
                        <span className="inline-block w-2 h-4 bg-vibe-accent animate-pulse ml-1" />
                      )}
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap">{message.content}</div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Quick Actions — E.21.32 (2026-05-04): chip buttons insert templates
          into the textarea at the current cursor position. The chips are
          context-aware: `@file` references the currently active file by
          name (or literal `@` if no file is open). The implied actions
          (run /generate, query @docs) fire when the user submits the
          message — the chips are pure text-shortcuts in v0.1. */}
      <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-t border-vibe-border overflow-hidden">
        <button
          className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-vibe-bg-tertiary hover:bg-vibe-border text-vibe-text-secondary shrink-0"
          title="Reference current file"
          onClick={() => insertChipTemplate('@file')}
        >
          <AtSign className="w-3 h-3" />
          <span className="hidden sm:inline">file</span>
        </button>
        <button
          className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-vibe-bg-tertiary hover:bg-vibe-border text-vibe-text-secondary shrink-0"
          title="Generate code"
          onClick={() => insertChipTemplate('/generate')}
        >
          <FileCode className="w-3 h-3" />
          <span className="hidden sm:inline">/generate</span>
        </button>
        <button
          className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-vibe-bg-tertiary hover:bg-vibe-border text-vibe-text-secondary shrink-0"
          title="Query BRAIN docs"
          onClick={() => insertChipTemplate('@docs')}
        >
          <BookOpen className="w-3 h-3" />
          <span className="hidden sm:inline">@docs</span>
        </button>
      </div>

      {/* Input */}
      <div className="p-3 border-t border-vibe-border">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask me anything..."
            rows={1}
            className="flex-1 bg-vibe-bg-tertiary text-vibe-text rounded-lg px-3 py-2 text-sm resize-none outline-none focus:ring-1 focus:ring-vibe-accent min-h-[40px] max-h-[120px]"
            style={{
              height: 'auto',
              minHeight: '40px',
            }}
          />
          {isStreaming ? (
            <button
              onClick={handleStopStreaming}
              className="p-2 rounded-lg bg-vibe-error text-white"
            >
              <StopCircle className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!input.trim()}
              className={clsx(
                'p-2 rounded-lg transition-colors',
                input.trim()
                  ? 'bg-vibe-accent text-white hover:bg-vibe-accent-hover'
                  : 'bg-vibe-bg-tertiary text-vibe-text-secondary cursor-not-allowed'
              )}
            >
              <Send className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChatPanelLegacy;
